import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import useMostPopulatedSearchExperienceFacets, {
  type CategoryData,
} from '../hooks/useMostPopulatedSearchExperienceFacets'
import { arrayToObject } from 'lib/array/arrayUtils'
import { sortCategoriesByRelevance } from 'lib/experiences/categoryRelevanceRanking'
import {
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
} from 'constants/experience'
import {
  EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID,
  EXP_CATEGORY_TRANSFERS_SEARCH_ID,
  getNextCategoryCodesForSearchCategory,
  splitTravelEssentialsCategoryGroups,
} from './experienceSearchTravelEssentialsSplit'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'
import useTrackExperienceSearchFilter from 'components/Experiences/hooks/useTrackExperienceSearchFilter'

const EXCLUDED_QUICK_FILTER_CATEGORY_IDS = [
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
]

interface CategoryFilterChipProps {
  categoryData: CategoryData;
  categoryFacetsById: Record<number, App.ExperienceCategoryFacet>;
  selectedCategoryCodes: Array<number>;
  onApply: (
    parentId: number,
    childIds: Array<number>,
    selectedIds: Set<number>,
  ) => void;
  onReset: (parentId: number, childIds: Array<number>) => void;
  isMobile: boolean;
}

function CategoryFilterChip({
  categoryData,
  categoryFacetsById,
  selectedCategoryCodes,
  onApply,
  onReset,
  isMobile,
}: CategoryFilterChipProps) {
  const intl = useIntl()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const [isOpen, toggleOpen, , closeDropdown] = useToggle()
  const chipRef = useRef<HTMLButtonElement>(null)

  const childCategories = categoryData.children
  const parentId = categoryData.parent.id
  const childIds = useMemo(
    () => childCategories.map((c) => c.id),
    [childCategories],
  )

  const allRelatedIds = useMemo(
    () => [parentId, ...childIds],
    [parentId, childIds],
  )
  const hasSelection = allRelatedIds.some((id) =>
    selectedCategoryCodes.includes(id),
  )

  const startIcon = useMemo(() => {
    if (parentId === EXP_CATEGORY_TRANSFERS_SEARCH_ID) {
      return <LineCarIcon />
    }
    if (parentId === EXP_CATEGORY_LOUNGES_AND_SERVICES_SEARCH_ID) {
      return <LinePlaneDepartureIcon />
    }
    return undefined
  }, [parentId])

  const [pendingChecked, setPendingChecked] = useState<Set<number>>(() => {
    const isParentSelected = selectedCategoryCodes.includes(parentId)
    if (isParentSelected) {
      return new Set(childIds)
    }
    return new Set(childIds.filter((id) => selectedCategoryCodes.includes(id)))
  })

  useEffect(() => {
    if (isOpen) {
      const isParentSelected = selectedCategoryCodes.includes(parentId)
      if (isParentSelected) {
        setPendingChecked(new Set(childIds))
      } else {
        setPendingChecked(
          new Set(childIds.filter((id) => selectedCategoryCodes.includes(id))),
        )
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleToggle = useCallback((value: number) => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      return next
    })
  }, [])

  const handleApply = useCallback(() => {
    const filterContexts = childCategories
      .filter((child) => {
        const wasSelected = selectedCategoryCodes.includes(parentId) ||
          selectedCategoryCodes.includes(child.id)
        return wasSelected !== pendingChecked.has(child.id)
      })
      .map((child) => ({
        label: child.name,
        type: 'category' as const,
        value: pendingChecked.has(child.id) ? 'selected' : 'unselected',
      }))
    trackSearchFilter(filterContexts)
    onApply(parentId, childIds, pendingChecked)
    closeDropdown()
  }, [childCategories, selectedCategoryCodes, parentId, pendingChecked, trackSearchFilter, onApply, childIds, closeDropdown])

  const handleReset = useCallback(() => {
    if (hasSelection) {
      trackSearchFilter({
        label: categoryData.parent.name,
        type: 'category',
        value: 'cleared',
      })
    }
    onReset(parentId, childIds)
    closeDropdown()
  }, [categoryData.parent.name, hasSelection, trackSearchFilter, onReset, parentId, childIds, closeDropdown])

  const applyText = intl.formatMessage({
    id: 'XGAxBfjicX0x40wpm7Xt',
    defaultMessage: 'Apply',
    description:
      'Button text to apply sort and filter selections in experience search',
  })

  const resetText = intl.formatMessage({
    id: '1BCuuKA_YbmI7wo_U3rn',
    defaultMessage: 'Reset',
    description:
      'Link to reset filter selections in the search or listing filters panel',
  })

  const visibleChildren = useMemo(
    () =>
      childCategories.filter((c) => (categoryFacetsById[c.id]?.count ?? 0) > 0),
    [childCategories, categoryFacetsById],
  )

  const allSelected = useMemo(
    () =>
      visibleChildren.length > 0 &&
      visibleChildren.every((c) => pendingChecked.has(c.id)),
    [visibleChildren, pendingChecked],
  )

  const handleSelectAll = useCallback(() => {
    setPendingChecked((prev) => {
      const next = new Set(prev)
      if (visibleChildren.every((c) => next.has(c.id))) {
        visibleChildren.forEach((c) => next.delete(c.id))
      } else {
        visibleChildren.forEach((c) => next.add(c.id))
      }
      return next
    })
  }, [visibleChildren])

  const checkboxList = (
    <>
      {visibleChildren.length > 0 && (
        <CheckboxInputListItem
          inputPlacement="start"
          checked={allSelected}
          onChange={handleSelectAll}
          title={intl.formatMessage({
            id: 'VTp-nGNzNfgb6HumlrZx',
            defaultMessage: 'Select all',
            description:
              'Checkbox to select all subcategory options in a category filter dropdown',
          })}
          size="medium"
        />
      )}
      {visibleChildren.map((child) => {
        const count = categoryFacetsById[child.id]?.count ?? 0
        return (
          <CheckboxInputListItem
            key={child.id}
            inputPlacement="start"
            checked={pendingChecked.has(child.id)}
            onChange={() => handleToggle(child.id)}
            title={child.name}
            size="medium"
          >
            <BodyText variant="medium" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'CV8utsl6cVxsfdk50i1a',
                  defaultMessage: '({count})',
                  description:
                    'Count of available items shown next to a category filter option',
                },
                { count },
              )}
            </BodyText>
          </CheckboxInputListItem>
        )
      })}
    </>
  )

  return (
    <div>
      <FilterChip
        ref={chipRef}
        size="medium"
        selected={hasSelection}
        onClick={toggleOpen}
        startIcon={startIcon}
        endIcon={isOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {categoryData.parent.name}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="S"
          width="max"
          triggerRef={chipRef}
          anchorRef={chipRef}
          open={isOpen}
          placement="bottom-start"
          onClose={closeDropdown}
          title={categoryData.parent.name}
          hasDismissButton
          primaryActionProps={{
            children: applyText,
            onClick: handleApply,
            style: { flex: 1 },
          }}
          secondaryActionProps={{
            kind: 'tertiary',
            children: resetText,
            onClick: handleReset,
          }}
        >
          {checkboxList}
        </DropdownList>
      </CSSBreakpoint>
      <CSSBreakpoint max="mobile">
        <ModalBase isOpen={isOpen && isMobile} onClose={closeDropdown}>
          <ModalHeader
            title={categoryData.parent.name}
            onCloseButtonClick={closeDropdown}
          />
          <ModalBody>
            <ModalContent flushListItems>{checkboxList}</ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: applyText,
              onClick: handleApply,
              style: { flexGrow: 2 },
            }}
            secondaryActionProps={{
              children: resetText,
              onClick: handleReset,
              style: { flexGrow: 0 },
            }}
          />
        </ModalBase>
      </CSSBreakpoint>
    </div>
  )
}

interface Props {
  toggleFilterPanel: () => void;
  place?: App.Place;
}

function ExperienceExposedFiltersHeader({ toggleFilterPanel, place }: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const { isAnyVariant: discoveryRevampEnabled } = useExperiment(
    Experiments.experiencesSearchDiscoveryRevamp,
  )
  const { searchFilters, setCategoryCodesSearchFilter } =
    useExperiencesSearchFilters()
  const { primaryCategories, categories: categoryFacets } =
    useMostPopulatedSearchExperienceFacets(place, {
      rankCategoriesByRelevance: discoveryRevampEnabled,
      prioritiseSelectedCategories: discoveryRevampEnabled,
      sendPerformanceVariant: discoveryRevampEnabled,
    })

  const categoryFacetsById = useMemo(
    () => arrayToObject(categoryFacets, (f) => f.categoryId),
    [categoryFacets],
  )

  const selectedCategoryCodes = useMemo(
    () => searchFilters.categoryCodes ?? [],
    [searchFilters.categoryCodes],
  )

  const travelEssentialsSplitLabels = useMemo(() => ({
    transfers: intl.formatMessage({
      id: 'WGj8M709yLcgGiD9hdij',
      defaultMessage: 'Transfers',
      description: 'Experience search category label for transfer-related travel essentials',
    }),
    loungesAndServices: intl.formatMessage({
      id: 'x8JnpWwJHaV5vW0mX-B0',
      defaultMessage: 'Lounges and Services',
      description: 'Experience search category label for lounge and airport service travel essentials',
    }),
  }), [intl])

  const topLevelCategories = useMemo(() => {
    const eligible = primaryCategories.filter(
      (c) => !EXCLUDED_QUICK_FILTER_CATEGORY_IDS.includes(c.parent.id),
    )
    const categoryFacetsById = arrayToObject(categoryFacets, (f) => f.categoryId)
    let displayCategories = eligible

    // Search-page A/B only: present virtual Transfers/Lounges groups while
    // retaining the real Travel Essentials category IDs for filtering.
    if (discoveryRevampEnabled) {
      displayCategories = splitTravelEssentialsCategoryGroups(
        eligible,
        travelEssentialsSplitLabels,
        categoryFacetsById,
      )
    }

    return sortCategoriesByRelevance(
      place,
      displayCategories.map((categoryData) => ({
        ...categoryData,
        categoryId: categoryData.parent.id,
        name: categoryData.parent.name,
        relevanceScore: categoryData.relevanceScore,
        relatedCategoryIds: [
          categoryData.parent.id,
          ...categoryData.children.map((child) => child.id),
        ],
      })),
      undefined,
      {
        selectedCategoryIds: discoveryRevampEnabled ? selectedCategoryCodes : [],
        useRelevanceRanking: discoveryRevampEnabled,
      },
    )
  }, [place, discoveryRevampEnabled, primaryCategories, categoryFacets, selectedCategoryCodes, travelEssentialsSplitLabels])

  const onCategoryApply = useCallback(
    (parentId: number, childIds: Array<number>, selectedIds: Set<number>) => {
      const current = searchFilters.categoryCodes ?? []
      setCategoryCodesSearchFilter(getNextCategoryCodesForSearchCategory(
        current,
        parentId,
        childIds,
        Array.from(selectedIds),
      ))
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const onCategoryReset = useCallback(
    (parentId: number, childIds: Array<number>) => {
      const current = searchFilters.categoryCodes ?? []
      setCategoryCodesSearchFilter(getNextCategoryCodesForSearchCategory(
        current,
        parentId,
        childIds,
        [],
      ))
    },
    [searchFilters.categoryCodes, setCategoryCodesSearchFilter],
  )

  const allFiltersLabel = intl.formatMessage({
    id: '-lpIiE7WOtNg4S1Miji_',
    defaultMessage: 'Sort & Filters',
    description:
      'Label for the sort and filters chip in experience search exposed filters',
  })

  return (
    <Carousel gap={8} gutterStyle="overflow" gutterSize={16}>
      <FilterChip
        size="medium"
        startIcon={<LineFilterAltIcon />}
        onClick={toggleFilterPanel}
      >
        {allFiltersLabel}
      </FilterChip>

      {topLevelCategories.map((cat) => (
        <CategoryFilterChip
          key={cat.parent.id}
          categoryData={cat}
          categoryFacetsById={categoryFacetsById}
          selectedCategoryCodes={selectedCategoryCodes}
          onApply={onCategoryApply}
          onReset={onCategoryReset}
          isMobile={isMobile}
        />
      ))}
    </Carousel>
  )
}

export default ExperienceExposedFiltersHeader
